// Tenantra Inc. - Public reference questionnaire, opened by the applicant's reference from an emailed link
import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Logo } from '@/components/Logo';
import api from '@/services/api';

const errText = (err) => { const d = err?.response?.data?.detail; return typeof d === 'string' ? d : Array.isArray(d) ? d.map(x => x?.msg).join(' ') : 'Something went wrong. Please try again.'; };

function Choice({ label, options, value, onChange, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-sm font-medium text-stone-800 mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map(([v, l]) => (
          <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testId}-${v}`}
            className={`px-3.5 h-9 rounded-full border text-sm transition-colors ${value === v ? 'bg-teal-600 border-teal-600 text-white' : 'bg-white border-stone-300 text-stone-700 hover:border-stone-400'}`}>
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}

const Input = (props) => <input {...props} className="w-full h-11 px-3 rounded-lg border border-stone-300 text-base text-stone-900 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500" />;
const Label = ({ children }) => <label className="block text-xs text-stone-500 mb-1">{children}</label>;

export default function ReferencePage() {
  const token = window.location.pathname.split('/').pop();
  const [info, setInfo] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [f, setF] = useState({ confirm_name: '', confirm_phone: '', confirm_address: '', paid_on_time: '', issues: '', issues_detail: '', rent_again: '', comments: '' });
  const set = (p) => { setF(x => ({ ...x, ...p })); setError(''); };

  useEffect(() => {
    api.get(`/references/${token}`).then(r => {
      setInfo(r.data);
      setF(x => ({ ...x, confirm_name: r.data.applicant_name || '', confirm_phone: r.data.applicant_phone || '', confirm_address: r.data.applicant_address || '' }));
    }).catch(e => setLoadErr(errText(e)));
  }, [token]);

  const landlord = info?.reference_kind === 'current_landlord';
  const submit = async () => {
    if (!f.confirm_name.trim() || !f.confirm_phone.trim() || !f.confirm_address.trim()) return setError('Please confirm the name, phone and address.');
    if (!f.paid_on_time || !f.issues || !f.rent_again) return setError('Please answer every question.');
    setBusy(true);
    try { await api.post(`/references/${token}`, { ...f, issues_detail: f.issues_detail || null, comments: f.comments || null }); setDone(true); window.scrollTo(0, 0); }
    catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200"><div className="max-w-xl mx-auto px-4 h-14 flex items-center"><Logo /></div></header>
      <main className="max-w-xl mx-auto px-4 py-8">{children}</main>
    </div>
  );

  if (loadErr) return shell(<div className="bg-white border border-stone-200 rounded-lg p-6 text-center" data-testid="reference-link-error"><AlertCircle className="w-6 h-6 text-stone-400 mx-auto mb-2" /><p className="text-sm text-stone-700">{loadErr}</p></div>);
  if (!info) return shell(<div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>);
  if (done || info.already_responded) return shell(
    <div className="bg-white border border-stone-200 rounded-lg p-8 text-center" data-testid="reference-success">
      <CheckCircle2 className="w-10 h-10 text-teal-600 mx-auto mb-3" />
      <h1 className="text-xl font-semibold text-stone-900">Thank you</h1>
      <p className="text-sm text-stone-600 mt-2 max-w-sm mx-auto">Your reference for {info.applicant_name} has been sent to {info.manager_name}. You can close this page.</p>
    </div>
  );

  return shell(
    <div className="space-y-6" data-testid="reference-page">
      <div>
        <h1 className="text-2xl font-semibold text-stone-900">Reference for {info.applicant_name}</h1>
        <p className="text-sm text-stone-600 mt-1">{info.manager_name} is reviewing a rental application and {info.applicant_name} gave your name as a {landlord ? 'landlord' : 'personal'} reference. These answers go only to the property manager. No exact dates needed.</p>
      </div>

      <section className="bg-white border border-stone-200 rounded-lg p-5 space-y-4">
        <h2 className="text-sm font-semibold text-stone-900">First, confirm who this is about</h2>
        <div><Label>Their full name</Label><Input value={f.confirm_name} onChange={e => set({ confirm_name: e.target.value })} data-testid="reference-confirm-name" /></div>
        <div><Label>Phone number you know them by</Label><Input value={f.confirm_phone} onChange={e => set({ confirm_phone: e.target.value })} data-testid="reference-confirm-phone" /></div>
        <div><Label>{landlord ? 'Address they rented' : 'Their address, as you know it'}</Label><Input value={f.confirm_address} onChange={e => set({ confirm_address: e.target.value })} data-testid="reference-confirm-address" /></div>
        <p className="text-xs text-stone-400">We pre-filled these from the application. Correct anything that looks wrong.</p>
      </section>

      <section className="bg-white border border-stone-200 rounded-lg p-5 space-y-5">
        <Choice testId="reference-paid" label={landlord ? 'Did they pay rent on time?' : 'As far as you know, did they pay their rent and bills on time?'}
          value={f.paid_on_time} onChange={v => set({ paid_on_time: v })}
          options={[['yes', 'Always on time'], ['mostly', 'Mostly on time'], ['no', 'Often late'], ['not_sure', 'Not sure']]} />
        <Choice testId="reference-issues" label="Any damage, complaints or problems?"
          value={f.issues} onChange={v => set({ issues: v })}
          options={[['none', 'None'], ['minor', 'Minor'], ['major', 'Major']]} />
        {(f.issues === 'minor' || f.issues === 'major') && (
          <div><Label>Please describe</Label><textarea value={f.issues_detail} onChange={e => set({ issues_detail: e.target.value })} rows={3} data-testid="reference-issues-detail"
            className="w-full px-3 py-2 rounded-lg border border-stone-300 text-base text-stone-900 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500" /></div>
        )}
        <Choice testId="reference-again" label={landlord ? 'Would you rent to them again?' : 'Would you recommend them as a tenant?'}
          value={f.rent_again} onChange={v => set({ rent_again: v })}
          options={[['yes', 'Yes'], ['no', 'No'], ['unsure', 'Unsure']]} />
        <div><Label>Anything else the property manager should know? (optional)</Label>
          <textarea value={f.comments} onChange={e => set({ comments: e.target.value })} rows={4} data-testid="reference-comments"
            className="w-full px-3 py-2 rounded-lg border border-stone-300 text-base text-stone-900 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500" /></div>
      </section>

      {error && <div className="p-2.5 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-red-600 text-sm" data-testid="reference-error"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}
      <button type="button" onClick={submit} disabled={busy} data-testid="reference-submit"
        className="w-full justify-center h-12 px-6 rounded-xl active:scale-[0.98] transition-[background-color,transform] bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}Send reference
      </button>
    </div>
  );
}
